import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  RawInput,
} from '@payaca/ambrosia';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import useSetProposalDiscount from '@/api/mutations/proposals/useSetProposalDiscount';
import {
  ProposalDiscountFormValues,
  proposalDiscountFormSchema,
} from '@/ui/pages/proposalBuilderPage/components/proposalDiscountSchema';

const EMPTY_VALUES: ProposalDiscountFormValues = {
  percentage: '',
  description: '',
};

export type ProposalDiscountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  projectId: string;
  /** Title for the dialogue, so it matches the action that opened it. */
  title: string;
};

/**
 * Editor for the Proposal-level discount. The discount is a single percentage
 * applied to every line item on the Proposal; setting 0% removes it. The form
 * always opens fresh — there is no canonical Proposal-level percentage to
 * pre-fill, as the discount lives on the line items.
 */
const ProposalDiscountModal: FC<ProposalDiscountModalProps> = (props) => {
  const { isOpen, onClose, proposalId, projectId, title } = props;

  const { mutate, isPending } = useSetProposalDiscount({
    proposalId,
    projectId,
  });

  const formMethods = useForm<ProposalDiscountFormValues>({
    defaultValues: EMPTY_VALUES,
    resolver: zodResolver(proposalDiscountFormSchema),
    mode: 'onTouched',
  });
  const { reset } = formMethods;

  useEffect(() => {
    if (isOpen) {
      reset(EMPTY_VALUES);
    }
  }, [isOpen, reset]);

  const handleSubmit = (formValues: ProposalDiscountFormValues) => {
    const percentage = Number(formValues.percentage);

    mutate(
      {
        proposalId,
        discountPercentage: percentage,
        // Clear any description when the discount is removed (0%).
        discountDescription:
          percentage === 0 ? null : formValues.description?.trim() || null,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <Form methods={formMethods} onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-4">
            This discount is applied to every line item on the proposal. Set 0%
            to remove it.
          </p>

          <Fieldset>
            <Field name="percentage">
              <Field.Label>Discount (%)</Field.Label>
              <RawInput
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                max={100}
                {...formMethods.register('percentage')}
              />
            </Field>

            <Field name="description">
              <Field.Label>Description (optional)</Field.Label>
              <RawInput {...formMethods.register('description')} />
            </Field>
          </Fieldset>
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isProcessing={isPending} disabled={isPending}>
              Save discount
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ProposalDiscountModal;
